const META_KEYS = new Set([
  "client_id",
  "service_id",
  "email",
  "menu",
  "submenu",
  "segment_type",
  "segment_id",
  "segment_name",
  "show_type",
  "parameters",
  "perform_download",
  "url",
  "filter_type",
  "conversation_ids",
  "customer_ids",
]);

const PAGINATION_KEYS = ["limit", "page_size", "page_number", "page", "offset"];

const PRESET_DATE_FIELDS = new Set([
  "call_time",
  "last_interaction_time",
  "first_interaction_time",
  "crm_lead_created_at",
  "lead_created_at",
]);

function isEmpty(value) {
  return (
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  );
}

// Tags 4-30 are newer metadata filters. Keep them in the same structured
// filter channel used by the dashboard endpoints so callers do not need to
// change their long positional API signatures.
export const buildAdditionalTagFilterConditions = (filters = {}) =>
  Array.from({ length: 27 }, (_, index) => `tag${index + 4}`).reduce((conditions, field) => {
    const value = filters[field];
    if (isEmpty(value)) return conditions;
    const normalizedValue = Array.isArray(value)
      ? value.filter((item) => !isEmpty(item))
      : value;
    if (isEmpty(normalizedValue)) return conditions;
    conditions.push({
      field,
      operator: Array.isArray(normalizedValue) ? "in" : "eq",
      value: normalizedValue,
    });
    return conditions;
  }, []);

export const getAdditionalTagFilterValues = (filters = {}) =>
  Array.from({ length: 27 }, (_, index) => filters[`tag${index + 4}`]);

function toSnakeCase(value) {
  return String(value || "")
    .trim()
    .replace(/([A-Z])/g, "_$1")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();
}

function normalizeLegacyParameterFilters(payload) {
  const normalized = [];
  const agentFeedback = payload.agent_feedback_parameter;
  const businessInsight = payload.business_insight_parameter;
  const customModuleParameterName = payload.custom_module_parameter_name;
  const customModuleParameterAnswer = payload.custom_module_parameter_answer;

  if (Array.isArray(agentFeedback)) {
    agentFeedback.forEach((item) => {
      if (isStructuredCondition(item)) {
        normalized.push(item);
        return;
      }
      if (!item || typeof item !== "object") return;
      const field = toSnakeCase(item.agent_feedback_parameter);
      const lower = item.agent_feedback_parameter_score_lower;
      const upper = item.agent_feedback_parameter_score_upper;
      if (isEmpty(field) || (isEmpty(lower) && isEmpty(upper))) return;
      normalized.push({
        field,
        operator: "between",
        value: { lower, upper },
        module: "agent_feedback",
      });
    });
  }

  if (Array.isArray(businessInsight)) {
    businessInsight.forEach((item) => {
      if (isStructuredCondition(item)) {
        normalized.push(item);
        return;
      }
      if (!item || typeof item !== "object") return;
      const field = toSnakeCase(item.business_insights_parameter);
      const value = item.business_insights_parameter_value;
      if (isEmpty(field) || isEmpty(value)) return;
      normalized.push({
        field,
        operator: Array.isArray(value) ? "in" : "eq",
        value,
        module: "business_insight",
      });
    });
  }

  if (!isEmpty(customModuleParameterName) && !isEmpty(customModuleParameterAnswer)) {
    normalized.push({
      field: toSnakeCase(customModuleParameterName),
      operator: Array.isArray(customModuleParameterAnswer) ? "in" : "eq",
      value: customModuleParameterAnswer,
      module: "custom_module",
    });
  }

  return normalized;
}

function toArray(value) {
  if (Array.isArray(value)) return value.filter((item) => !isEmpty(item));
  return [value];
}

function parseDurationRange(value) {
  if (typeof value !== "string") return null;
  const nums = value.match(/\d+(\.\d+)?/g);
  if (!nums || nums.length < 2) return null;
  const lower = Number(nums[0]);
  const upper = Number(nums[1]);
  if (Number.isNaN(lower) || Number.isNaN(upper)) return null;
  return { lower, upper };
}

function createPagination(payload) {
  const limit = Number(payload.page_size ?? payload.limit ?? 50) || 50;
  const page_number = Number(payload.page_number ?? payload.page ?? 1) || 1;
  const offset = Number(payload.offset ?? (page_number - 1) * limit) || 0;
  return { limit, offset, page_number };
}

function pushCondition(and, field, operator, value) {
  if (isEmpty(value)) return;
  and.push({ field, operator, value });
}

function isStructuredCondition(value) {
  return (
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    typeof value.field === "string" &&
    typeof value.operator === "string" &&
    "value" in value
  );
}

function pushStructuredCondition(and, condition) {
  if (!isStructuredCondition(condition)) return;
  const entry = {
    field: condition.field,
    operator: condition.operator,
    value: condition.value,
  };
  if (!isEmpty(condition.module)) {
    entry.module = condition.module;
  }
  if (isEmpty(entry.value)) return;
  and.push(entry);
}

export function buildDashboardFilterPayload(rawPayload = {}) {
  const ft = String(rawPayload.filter_type ?? rawPayload.filterType ?? "").trim();
  if (ft === "conversation_ids" || ft === "customer_ids") {
    const requestPayload = {
      pagination: createPagination(rawPayload),
      filter: { and: [] },
    };
    const conv = Array.isArray(rawPayload.conversation_ids)
      ? rawPayload.conversation_ids.map((x) => (x == null ? "" : String(x).trim())).filter(Boolean)
      : [];
    const cust = Array.isArray(rawPayload.customer_ids)
      ? rawPayload.customer_ids.map((x) => (x == null ? "" : String(x).trim())).filter(Boolean)
      : [];
    META_KEYS.forEach((metaKey) => {
      if (metaKey === "filter_type" || metaKey === "conversation_ids" || metaKey === "customer_ids") return;
      if (!isEmpty(rawPayload[metaKey])) {
        requestPayload[metaKey] = rawPayload[metaKey];
      }
    });
    requestPayload.filter_type = ft;
    requestPayload.conversation_ids = ft === "conversation_ids" ? conv : [];
    requestPayload.customer_ids = ft === "customer_ids" ? cust : [];
    return requestPayload;
  }

  const payload = { ...rawPayload };
  const and = [];
  const moduleParameterFilters = payload.module_parameter_filters ?? payload.parameter_filters;
  const hasStructuredModuleFilters = Array.isArray(moduleParameterFilters) && moduleParameterFilters.length > 0;
  if (!hasStructuredModuleFilters) {
    const legacyParameterFilters = normalizeLegacyParameterFilters(payload);
    legacyParameterFilters.forEach((condition) => pushStructuredCondition(and, condition));
  }
  delete payload.agent_feedback_parameter;
  delete payload.business_insight_parameter;
  delete payload.custom_module_parameter_name;
  delete payload.custom_module_parameter_answer;
  if (Array.isArray(moduleParameterFilters)) {
    moduleParameterFilters.forEach((condition) => pushStructuredCondition(and, condition));
  }
  delete payload.module_parameter_filters;
  delete payload.parameter_filters;
  // 1) Score range and duration range
  const scoreLower = payload.score_lower ?? payload.scoreLower;
  const scoreUpper = payload.score_upper ?? payload.scoreUpper;
  if (!isEmpty(scoreLower) || !isEmpty(scoreUpper)) {
    pushCondition(and, "overall_score", "between", {
      lower: isEmpty(scoreLower) ? 0 : Number(scoreLower),
      upper: isEmpty(scoreUpper) ? 100 : Number(scoreUpper),
    });
  }
  delete payload.score_lower;
  delete payload.score_upper;
  delete payload.scoreLower;
  delete payload.scoreUpper;

  // Audio duration:
  // - preset => duration_preset
  // - custom range string ("1-10") => between { lower, upper }
  const durationKey =
    payload.audio_duration !== undefined
      ? "audio_duration"
      : payload.audioDuration !== undefined
      ? "audioDuration"
      : null;
  if (durationKey) {
    const durationValue = payload[durationKey];
    if (!isEmpty(durationValue)) {
      const text = String(durationValue);
      const range = parseDurationRange(text);
      if (range && /^\s*\d+(\.\d+)?\s*-\s*\d+(\.\d+)?\s*$/.test(text)) {
        pushCondition(and, "audio_duration", "between", range);
      } else {
        pushCondition(and, "audio_duration", "duration_preset", durationValue);
      }
    }
  }
  delete payload.audio_duration;
  delete payload.audioDuration;

  const dateRangeConfig = {
    call_time: {
      value: payload.call_time ?? payload.callTime,
      lower: payload.custom_start ?? payload.customStart ?? payload.call_time_start ?? payload.callTimeStart,
      upper: payload.custom_end ?? payload.customEnd ?? payload.call_time_end ?? payload.callTimeEnd,
      rangeType: payload.custom_range_type ?? payload.customRangeType ?? payload.call_time_range_type ?? payload.callTimeRangeType,
    },
    last_interaction_time: {
      value:
        payload.last_interaction_time ??
        payload.lastInteractionTime ??
        payload.lastinteraction,
      lower:
        payload.last_interaction_custom_start ??
        payload.lastInteractionCustomStart ??
        payload.last_interaction_time_start ??
        payload.lastInteractionTimeStart ??
        payload.lastinteraction_start,
      upper:
        payload.last_interaction_custom_end ??
        payload.lastInteractionCustomEnd ??
        payload.last_interaction_time_end ??
        payload.lastInteractionTimeEnd ??
        payload.lastinteraction_end,
      rangeType:
        payload.last_interaction_range_type ??
        payload.lastInteractionRangeType ??
        payload.lastinteraction_range_type,
    },
    first_interaction_time: {
      value:
        payload.first_interaction_time ??
        payload.firstInteractionTime ??
        payload.firstinteraction,
      lower:
        payload.first_interaction_custom_start ??
        payload.firstInteractionCustomStart ??
        payload.first_interaction_time_start ??
        payload.firstInteractionTimeStart ??
        payload.firstinteraction_start,
      upper:
        payload.first_interaction_custom_end ??
        payload.firstInteractionCustomEnd ??
        payload.first_interaction_time_end ??
        payload.firstInteractionTimeEnd ??
        payload.firstinteraction_end,
      rangeType:
        payload.first_interaction_range_type ??
        payload.firstInteractionRangeType ??
        payload.firstinteraction_range_type,
    },
    crm_lead_created_at: {
      value: payload.crm_lead_created_at ?? payload.crmLeadCreatedAt ?? payload.lead_created_at ?? payload.leadCreatedAt,
      lower:
        payload.crm_lead_created_at_start ??
        payload.crmLeadCreatedAtStart ??
        payload.lead_created_at_start ??
        payload.leadCreatedAtStart,
      upper:
        payload.crm_lead_created_at_end ??
        payload.crmLeadCreatedAtEnd ??
        payload.lead_created_at_end ??
        payload.leadCreatedAtEnd,
      rangeType:
        payload.crm_lead_created_at_range_type ??
        payload.crmLeadCreatedAtRangeType ??
        payload.lead_created_at_range_type ??
        payload.leadCreatedAtRangeType,
    },
  };

  Object.entries(dateRangeConfig).forEach(([field, config]) => {
    const { value, lower, upper, rangeType } = config;
    if (isEmpty(value)) return;
    const normalized = String(value).toLowerCase();
    if (normalized.includes("custom")) {
      const normalizedRangeType = String(rangeType || "between").toLowerCase();
      const hasLower = !isEmpty(lower);
      const hasUpper = !isEmpty(upper);
      const shouldUseAfter = normalizedRangeType === "after" || (hasLower && !hasUpper);
      if (shouldUseAfter) {
        if (!isEmpty(lower)) {
          pushCondition(and, field, "after", lower);
        }
        return;
      }
      if (hasLower && hasUpper) {
        pushCondition(and, field, "between", { lower, upper });
      }
      return;
    }
    pushCondition(and, field, "date_preset", value);
  });

  delete payload.call_time;
  delete payload.callTime;
  delete payload.custom_start;
  delete payload.customStart;
  delete payload.custom_end;
  delete payload.customEnd;
  delete payload.custom_range_type;
  delete payload.customRangeType;
  delete payload.call_time_start;
  delete payload.callTimeStart;
  delete payload.call_time_end;
  delete payload.callTimeEnd;
  delete payload.call_time_range_type;
  delete payload.callTimeRangeType;
  delete payload.last_interaction_time;
  delete payload.lastInteractionTime;
  delete payload.last_interaction_custom_start;
  delete payload.lastInteractionCustomStart;
  delete payload.last_interaction_custom_end;
  delete payload.lastInteractionCustomEnd;
  delete payload.last_interaction_time_start;
  delete payload.lastInteractionTimeStart;
  delete payload.last_interaction_time_end;
  delete payload.lastInteractionTimeEnd;
  delete payload.lastinteraction;
  delete payload.lastinteraction_start;
  delete payload.lastinteraction_end;
  delete payload.lastinteraction_range_type;
  delete payload.last_interaction_range_type;
  delete payload.lastInteractionRangeType;
  delete payload.first_interaction_time;
  delete payload.firstInteractionTime;
  delete payload.first_interaction_custom_start;
  delete payload.firstInteractionCustomStart;
  delete payload.first_interaction_custom_end;
  delete payload.firstInteractionCustomEnd;
  delete payload.first_interaction_time_start;
  delete payload.firstInteractionTimeStart;
  delete payload.first_interaction_time_end;
  delete payload.firstInteractionTimeEnd;
  delete payload.firstinteraction;
  delete payload.firstinteraction_start;
  delete payload.firstinteraction_end;
  delete payload.firstinteraction_range_type;
  delete payload.first_interaction_range_type;
  delete payload.firstInteractionRangeType;
  delete payload.crm_lead_created_at;
  delete payload.crmLeadCreatedAt;
  delete payload.crm_lead_created_at_start;
  delete payload.crmLeadCreatedAtStart;
  delete payload.crm_lead_created_at_end;
  delete payload.crmLeadCreatedAtEnd;
  delete payload.lead_created_at;
  delete payload.leadCreatedAt;
  delete payload.lead_created_at_start;
  delete payload.leadCreatedAtStart;
  delete payload.lead_created_at_end;
  delete payload.leadCreatedAtEnd;
  delete payload.crm_lead_created_at_range_type;
  delete payload.crmLeadCreatedAtRangeType;
  delete payload.lead_created_at_range_type;
  delete payload.leadCreatedAtRangeType;

  // 2) Generic key parsing
  delete payload.filter_type;
  delete payload.conversation_ids;
  delete payload.customer_ids;

  Object.entries(payload).forEach(([rawKey, rawValue]) => {
    if (isEmpty(rawValue)) return;
    if (META_KEYS.has(rawKey) || PAGINATION_KEYS.includes(rawKey)) return;

    const key = toSnakeCase(rawKey);
    const value = rawValue;

    if (PRESET_DATE_FIELDS.has(key) && typeof value === "string") return;

    if (Array.isArray(value) && value.every((item) => isStructuredCondition(item))) {
      value.forEach((item) => pushStructuredCondition(and, item));
      return;
    }

    if (isStructuredCondition(value)) {
      pushStructuredCondition(and, value);
      return;
    }

    if (Array.isArray(value)) {
      pushCondition(and, key, "in", toArray(value));
      return;
    }

    if (typeof value === "object" && value && "start" in value && "end" in value) {
      pushCondition(and, key, "between", value);
      return;
    }

    pushCondition(and, key, "eq", value);
  });

  const uniqueAnd = [];
  const seenConditions = new Set();
  and.forEach((condition) => {
    const key = JSON.stringify([
      condition.field,
      condition.operator,
      condition.module || "",
      condition.value,
    ]);
    if (seenConditions.has(key)) return;
    seenConditions.add(key);
    uniqueAnd.push(condition);
  });

  const requestPayload = {
    pagination: createPagination(payload),
    filter: { and: uniqueAnd },
  };

  META_KEYS.forEach((metaKey) => {
    if (!isEmpty(rawPayload[metaKey])) {
      requestPayload[metaKey] = rawPayload[metaKey];
    }
  });

  return requestPayload;
}

function getPayloadFilterAnd(advancedFiltersPayload) {
  return Array.isArray(advancedFiltersPayload?.filter?.and) ? advancedFiltersPayload.filter.and : [];
}

function readLastInteractionFromAnd(and) {
  if (!Array.isArray(and) || and.length === 0) return null;

  const presetCond = and.find(
    (c) =>
      c &&
      c.field === "last_interaction_time" &&
      c.operator === "date_preset" &&
      c.value != null &&
      c.value !== ""
  );
  if (presetCond) {
    return {
      lastinteraction: String(presetCond.value),
      lastInteractionStart: "",
      lastInteractionEnd: "",
    };
  }

  const betweenCond = and.find(
    (c) => c && c.field === "last_interaction_time" && c.operator === "between" && c.value
  );
  if (betweenCond?.value && typeof betweenCond.value === "object") {
    const { lower, upper } = betweenCond.value;
    return {
      lastinteraction: "Custom Range",
      lastInteractionStart: lower != null && lower !== "" ? String(lower) : "",
      lastInteractionEnd: upper != null && upper !== "" ? String(upper) : "",
      lastinteraction_range_type: "between",
    };
  }

  const afterCond = and.find(
    (c) =>
      c &&
      c.field === "last_interaction_time" &&
      c.operator === "after" &&
      c.value != null &&
      c.value !== ""
  );
  if (afterCond) {
    return {
      lastinteraction: "Custom Range",
      lastInteractionStart: String(afterCond.value),
      lastInteractionEnd: "",
      lastinteraction_range_type: "after",
    };
  }

  return null;
}

/**
 * Last interaction preset/range as encoded in buildDashboardFilterPayload output
 * (optionally merged with top-level `lastinteraction` from Reports drilldown).
 */
export function extractLastInteractionForDrilldown(advancedFiltersPayload) {
  if (!advancedFiltersPayload || typeof advancedFiltersPayload !== "object") {
    return null;
  }
  const fromAnd = readLastInteractionFromAnd(getPayloadFilterAnd(advancedFiltersPayload));
  const top = advancedFiltersPayload.lastinteraction;
  if (top != null && String(top).trim() !== "") {
    const v = String(top).trim();
    if (v.toLowerCase().includes("custom")) {
      const start =
        advancedFiltersPayload["Last Interaction Start"] ||
        advancedFiltersPayload.last_interaction_custom_start ||
        advancedFiltersPayload.lastinteraction_start ||
        fromAnd?.lastInteractionStart ||
        "";
      const end =
        advancedFiltersPayload["Last Interaction End"] ||
        advancedFiltersPayload.last_interaction_custom_end ||
        advancedFiltersPayload.lastinteraction_end ||
        fromAnd?.lastInteractionEnd ||
        "";
      const explicitRangeType = String(
        advancedFiltersPayload.lastinteraction_range_type || fromAnd?.lastinteraction_range_type || ""
      ).toLowerCase();
      const rangeType =
        explicitRangeType === "after" || (start && !end) ? "after" : "between";
      return {
        lastinteraction: "Custom Range",
        lastInteractionStart: start,
        lastInteractionEnd: end,
        lastinteraction_range_type: rangeType,
      };
    }
    return { lastinteraction: v, lastInteractionStart: "", lastInteractionEnd: "" };
  }
  return fromAnd;
}

/**
 * First interaction preset/range (same encoding as `last_interaction_time` in `filter.and`
 * and/or top-level report advanced filter keys).
 */
function readFirstInteractionFromAnd(and) {
  if (!Array.isArray(and) || and.length === 0) return null;

  const presetCond = and.find(
    (c) =>
      c &&
      c.field === "first_interaction_time" &&
      c.operator === "date_preset" &&
      c.value != null &&
      c.value !== ""
  );
  if (presetCond) {
    return {
      firstinteraction: String(presetCond.value),
      firstInteractionStart: "",
      firstInteractionEnd: "",
    };
  }

  const betweenCond = and.find(
    (c) => c && c.field === "first_interaction_time" && c.operator === "between" && c.value
  );
  if (betweenCond?.value && typeof betweenCond.value === "object") {
    const { lower, upper } = betweenCond.value;
    return {
      firstinteraction: "Custom Range",
      firstInteractionStart: lower != null && lower !== "" ? String(lower) : "",
      firstInteractionEnd: upper != null && upper !== "" ? String(upper) : "",
      firstinteraction_range_type: "between",
    };
  }

  const afterCond = and.find(
    (c) =>
      c &&
      c.field === "first_interaction_time" &&
      c.operator === "after" &&
      c.value != null &&
      c.value !== ""
  );
  if (afterCond) {
    return {
      firstinteraction: "Custom Range",
      firstInteractionStart: String(afterCond.value),
      firstInteractionEnd: "",
      firstinteraction_range_type: "after",
    };
  }

  return null;
}

export function extractFirstInteractionForDrilldown(advancedFiltersPayload) {
  if (!advancedFiltersPayload || typeof advancedFiltersPayload !== "object") {
    return null;
  }
  const fromAnd = readFirstInteractionFromAnd(getPayloadFilterAnd(advancedFiltersPayload));
  const top = advancedFiltersPayload.firstinteraction;
  if (top != null && String(top).trim() !== "") {
    const v = String(top).trim();
    if (v.toLowerCase().includes("custom")) {
      const start =
        advancedFiltersPayload["First Interaction Start"] ||
        advancedFiltersPayload.firstinteraction_start ||
        advancedFiltersPayload.first_interaction_custom_start ||
        fromAnd?.firstInteractionStart ||
        "";
      const end =
        advancedFiltersPayload["First Interaction End"] ||
        advancedFiltersPayload.firstinteraction_end ||
        advancedFiltersPayload.first_interaction_custom_end ||
        fromAnd?.firstInteractionEnd ||
        "";
      const explicitRangeType = String(
        advancedFiltersPayload.firstinteraction_range_type || fromAnd?.firstinteraction_range_type || ""
      ).toLowerCase();
      const rangeType =
        explicitRangeType === "after" || (start && !end) ? "after" : "between";
      return {
        firstinteraction: "Custom Range",
        firstInteractionStart: start,
        firstInteractionEnd: end,
        firstinteraction_range_type: rangeType,
      };
    }
    return { firstinteraction: v, firstInteractionStart: "", firstInteractionEnd: "" };
  }
  return fromAnd;
}

/**
 * CRM lead created at preset/range (same encoding as other preset date fields in `filter.and`).
 */
function readCrmLeadCreatedAtFromAnd(and) {
  if (!Array.isArray(and) || and.length === 0) return null;

  const presetCond = and.find(
    (c) =>
      c &&
      c.field === "crm_lead_created_at" &&
      c.operator === "date_preset" &&
      c.value != null &&
      c.value !== ""
  );
  if (presetCond) {
    return {
      crm_lead_created_at: String(presetCond.value),
      crm_lead_created_at_start: "",
      crm_lead_created_at_end: "",
    };
  }

  const betweenCond = and.find(
    (c) => c && c.field === "crm_lead_created_at" && c.operator === "between" && c.value
  );
  if (betweenCond?.value && typeof betweenCond.value === "object") {
    const { lower, upper } = betweenCond.value;
    return {
      crm_lead_created_at: "Custom Range",
      crm_lead_created_at_start: lower != null && lower !== "" ? String(lower) : "",
      crm_lead_created_at_end: upper != null && upper !== "" ? String(upper) : "",
      crm_lead_created_at_range_type: "between",
    };
  }

  const afterCond = and.find(
    (c) =>
      c &&
      c.field === "crm_lead_created_at" &&
      c.operator === "after" &&
      c.value != null &&
      c.value !== ""
  );
  if (afterCond) {
    return {
      crm_lead_created_at: "Custom Range",
      crm_lead_created_at_start: String(afterCond.value),
      crm_lead_created_at_end: "",
      crm_lead_created_at_range_type: "after",
    };
  }

  return null;
}

export function extractCrmLeadCreatedAtForDrilldown(advancedFiltersPayload) {
  if (!advancedFiltersPayload || typeof advancedFiltersPayload !== "object") {
    return null;
  }
  const fromAnd = readCrmLeadCreatedAtFromAnd(getPayloadFilterAnd(advancedFiltersPayload));
  const top = advancedFiltersPayload.crm_lead_created_at;
  if (top != null && String(top).trim() !== "") {
    const v = String(top).trim();
    if (v.toLowerCase().includes("custom")) {
      const start =
        advancedFiltersPayload.crm_lead_created_at_start ||
        advancedFiltersPayload.crmLeadCreatedAtStart ||
        advancedFiltersPayload.lead_created_at_start ||
        advancedFiltersPayload.leadCreatedAtStart ||
        fromAnd?.crm_lead_created_at_start ||
        "";
      const end =
        advancedFiltersPayload.crm_lead_created_at_end ||
        advancedFiltersPayload.crmLeadCreatedAtEnd ||
        advancedFiltersPayload.lead_created_at_end ||
        advancedFiltersPayload.leadCreatedAtEnd ||
        fromAnd?.crm_lead_created_at_end ||
        "";
      const explicitRangeType = String(
        advancedFiltersPayload.crm_lead_created_at_range_type ||
          fromAnd?.crm_lead_created_at_range_type ||
          ""
      ).toLowerCase();
      const rangeType =
        explicitRangeType === "after" || (start && !end) ? "after" : "between";
      return {
        crm_lead_created_at: "Custom Range",
        crm_lead_created_at_start: start,
        crm_lead_created_at_end: end,
        crm_lead_created_at_range_type: rangeType,
      };
    }
    return { crm_lead_created_at: v, crm_lead_created_at_start: "", crm_lead_created_at_end: "" };
  }
  return fromAnd;
}
